import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { getElectricDataDuration } from "../../../model/slice/dataDurationSlice";
import {
  fetchGeneratorEngineData,
  fetchShaftGeneratorEngineData,
} from "../../../api/baseEngineData";
import ElectricWorker from "./worker/electricGraphData.worker";
import * as Comlink from "comlink";
import { calDurations } from "../../../constants/calDuration";
import { logger } from "../../../api/logger";
import { timeKey } from "../../../constants/timeKey";
import { currentVesselSelector } from "../../../model/slice/vesselSlice";
import { FETCH_CANCEL_MESSAGE, VESSEL_TYPE } from "../../../constants/constants";
import { ELECTRIC_TREND_TYPE } from "../../../constants/trendGraph/electricLines";
import { fetchChannelNames, fetchTrendDataByChannelNos } from "../../../api/channel";
import { ADDITIONAL_ELECTRIC_TREND_LIST } from "../../../constants/trendGraph/additionalElectricTrend";

let cancelToken;

const processTrendData = (data, targetCommonNames) => {
  const trendData = data.data.map((e) => {
    const tempData = {};
    e.data.forEach((data, i) => {
      Object.assign(tempData, {[targetCommonNames[i]]: Number(data.value)})
    })
    return {
      dateTime: e.dateTime,
      ...tempData,
    }
  })
  return trendData;
}

const fetchTrendData = async (vesselId, chNos, startDate, endDate, forceOneSec = false, signal) => {
  const interval = forceOneSec ? 0 : calDurations({ startDate, endDate });
  const convertChNosToStr = chNos.join(',');
  const data = await fetchTrendDataByChannelNos(vesselId, convertChNosToStr, startDate, endDate, interval, signal);
  return data.trendData;
}

const processChannelNos = (channelLists, selectedChart) => {
  const targetCommonNames = ADDITIONAL_ELECTRIC_TREND_LIST.find((chart) => chart.id === selectedChart).commonNames;
  const chNos = [];
  const existingCommonNames = [];
  targetCommonNames.forEach((commonName) => {
    const channel = channelLists?.names.find((channel) => channel.commonName === commonName);
    if(channel){
      chNos.push(channel.chNo);
      existingCommonNames.push(channel.commonName)
    }
  })
  return { chNos, existingCommonNames };
}

const getChannelNos = async (vesselId, selectedChart) => {
  const channelLists = await fetchChannelNames(vesselId, selectedChart);
  // this will integrate to new Electric Trend
  const returnedObj = processChannelNos(channelLists, selectedChart);
  return returnedObj;
}
export const getTrendData = async (vesselId, selectedChart, startDate, endDate, setIsLoading, forceOneSec = false, signal = null) => {
  setIsLoading(true);
  const { chNos, existingCommonNames } = await getChannelNos(vesselId, selectedChart);
  const fetchedData = await fetchTrendData(vesselId, chNos, startDate, endDate, forceOneSec, signal);
  const data = processTrendData(fetchedData, existingCommonNames);
  
  setIsLoading(false);
  return {data, config: fetchedData.config, commonNames: {commonNames: existingCommonNames}};
}

export const getTrendDataForDashboard = async (vesselId, selectedChart, channelLists, startDate, endDate, signal) => {
  const { chNos, existingCommonNames } = processChannelNos(channelLists, selectedChart);
  const fetchedData = await fetchTrendData(vesselId, chNos, startDate, endDate, false, signal);
  const data = processTrendData(fetchedData, existingCommonNames);
  return {data, config: fetchedData.config, commonNames: {commonNames: existingCommonNames}};
}

export const useElectricData = ({ vesselId = null, ge, sg, selectedChart, isVesselOverview, isSEPVessel }) => {
  const [graphData, setGraphData] = useState([]);
  const [graphConfig, setGraphConfig] = useState(null);
  const { startDate, endDate } = useSelector(getElectricDataDuration);
  const unmount = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isNoData, setIsNoData] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);
  const currentVessel = useSelector(currentVesselSelector);
  const fetchBase = [
    {
      getFetchData: async ({ startDate, endDate, interval }) =>
        await fetchGeneratorEngineData({
          vesselId,
          startDate,
          endDate,
          interval,
          cancelToken: cancelToken?.token,
        }),
      dataKey: "ge",
      fetchKey: "loadingData",
      data: [],
    },
    {
      // dateRangeKey: "dateRangeShaftGeneratorData",
      getFetchData: async ({ startDate, endDate, interval }) =>
        await fetchShaftGeneratorEngineData({
          vesselId,
          startDate,
          endDate,
          interval,
          cancelToken: cancelToken?.token,
        }),
      dataKey: "sg",
      fetchKey: "loadingData",
      data: [],
    },
  ];

  const getElectricValue = async (electricBase) => {
    const duration = calDurations({ startDate, endDate });
    const interval = currentVessel?.vesselType === VESSEL_TYPE.SEP && duration === 0 ? 0 : duration;

    logger.info(`getElectricValue -- GE: ${ge.length}, SG: ${sg.length}`);
    const promiseList = () => {
      if (electricBase.dataKey === "ge") {
        return ge === 0
          ? []
          : [electricBase.getFetchData({
                  startDate,
                  endDate,
                  interval,
                })
              ];
      } else if (electricBase.dataKey === "sg") {
        return sg === 0
          ? []
          : [electricBase.getFetchData({
                  startDate,
                  endDate,
                  interval,
                })
              ];
      }
      logger.info(`error data: ${electricBase.dataKey}`);
    };
    const fetchData = await Promise.all([...promiseList()]);
    const data = fetchData.map((d) => d[electricBase?.fetchKey]).flat();

    return {
      ...electricBase,
      data,
    };
  };

  const createNewGraphData = async ({ sgData, geData }) => {
    const worker = ElectricWorker();
    const wrap = Comlink.wrap(worker);
    const newData = await wrap?.createGraphData({
      sgData,
      geData,
    });
    const key = timeKey(newData);
    unmount.current ||
      setGraphData(newData.sort((a, b) => new Date(a[key]).getTime() - new Date(b[key]).getTime()));
    unmount.current || setIsNoData(!newData?.length);
    worker.terminate();
  };

  const dataLoad = async () => {
    logger.info(`dataLoad -- startData: ${startDate}, endDate: ${endDate}`);
    setIsLoading(true);
    const result = await Promise.all(fetchBase.map((d) => getElectricValue(d)));
    createNewGraphData({
      geData: result?.find((d) => d?.dataKey === "ge")?.data,
      sgData: result?.find((d) => d?.dataKey === "sg")?.data,
    }).then(() => {
      logger.info("dataLoad completed.");
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (ge === 0 && sg === 0) {
      setGraphData([]);
      setIsNoData(true);
      setIsLoading(false);
    }

    if (typeof cancelToken !== typeof undefined) {
      cancelToken.cancel(FETCH_CANCEL_MESSAGE);
    }

    cancelToken = axios.CancelToken.source();

    if (startDate === "" || vesselId === null || (ge === 0 && sg === 0)) return;
    setIsNoData(false);
    if(selectedChart === ELECTRIC_TREND_TYPE.ELECTRIC_POWER_TREND) {
      if(isSEPVessel) return
      dataLoad().then(() => {
        setGraphConfig(null)
      });
    }else{
      if(!isSEPVessel) return;
      getTrendData(vesselId, selectedChart, startDate, endDate, setIsLoading)
        .then((data) => {
          setGraphData(data.data);
          setGraphConfig({ ...data.config, ...data.commonNames });
        })
        .catch((err) => {
          logger.error(err);
        });
    }
  }, [vesselId, startDate, endDate, ge, sg, selectedChart]);

  useEffect(() => {
    return () => {
      unmount.current = true;
      setIsLoading(false);
    };
  }, []);

  useEffect(() => {
    if(graphData.length > 0) {
      setIsTimeout(false);
      return;
    }

    const timer = setTimeout(() => {
      setIsTimeout(true);
    }, 60000);
    return () => clearTimeout(timer);
  }, [graphData]);

  return {
    graphData,
    graphConfig,
    isLoading,
    isNoData,
    isTimeout,
  };
};
